import { Config } from 'config';
import { forOwn, map, assign, first } from 'lodash';
import { isArray } from 'gear';

import { Point, Matrix, Bounds, Color, Figure, Figures } from 'cdl';

let { Cx } = window;

export const toCoreCase = function( string ) {
  return string.charAt(0).toUpperCase() + string.substr(1);
};

export const objectToCoreCase = function(object) {
  const co = {};
  forOwn( object, (value,key) => {
    co[toCoreCase(key)] = value;
  });
  return co;
};

export const propertiesToCore = function(object) {
  const co = {};
  forOwn( object.prototype.Properties, (c,name) => {
    const v = object[name]();
    if( v !== undefined ) {
      co[ c.tag || toCoreCase(name) ] = v ;
    }
  });
  return co;
};

export const propertiesFromCore = function(object,rm) {
  const co = {};
  forOwn( object.prototype.Properties, (c,name) => {
    const v = rm[ c.tag || toCoreCase(name) ];
    if( v !== undefined ) {
      co[name] = v;
    }
  });
  return co;
};

export const objectFromCore = function(object,rm) {
  return new object( propertiesFromCore(object,rm) );
};

export const figureCacheModel = function(cacheId, transform, pin) {
  return {
      CacheId: cacheId,
      Pin: { X: pin.x, Y: pin.y },
      Transform: transform.toCore(),
      RelativePin: { X: pin.relX, Y: pin.relY },  // deprecate
      UseAnchor: pin.use !== "bounds",            // deprecate
      Constraint: { Width : 0, Height: 0 }        // deprecate
  };
};

Figure.cacheModel = function(figure,overwrite) {
  return figureCacheModel( figure._cacheId,
                           (overwrite && overwrite.matrix22) || figure._matrix22.toMatrix22(),
                           (overwrite && overwrite.pin) || figure._pin ); // do not use pin til bounds pinning is gone
};

Figure.toCoreModel = function( figure ) {
  return figure._cacheId ? { CachedCdl: Figure.cacheModel(figure) } :
                           { Cdl: figure.toXmlString( Config.serverCache ) };
};

let parseCoreMetrics = function(figure,metrics) {
  const cache = figure._cache;

  cache.set('prePin'         ,Point.fromCore(metrics.PreAnchor));
  cache.set('preVectorBounds',Bounds.fromCore(metrics.PreVectorBounds));
  cache.set('preBounds'      ,Bounds.fromCore(metrics.PreBounds));

  cache.set('matrix'      ,Matrix.fromCore(metrics.Matrix));
  cache.set('vectorBounds',Bounds.fromCore(metrics.VectorBounds));
  cache.set('bounds'      ,Bounds.fromCore(metrics.Bounds));

  if( Config.materialBounds && ! metrics.MaterialBounds ) {
    console.log('MaterialBounds not present');
  }
  cache.set('materialBounds', Cx.Bounds.fromCore(metrics.MaterialBounds || metrics.Bounds));


  if( metrics.GlobalPreMatrix ) {
    cache.set('globalPreMatrix'      ,Matrix.fromCore(metrics.GlobalPreMatrix));
    cache.set('globalPreVectorBounds',Bounds.fromCore(metrics.GlobalPreVectorBounds));
    cache.set('globalPreBounds'      ,Bounds.fromCore(metrics.GlobalPreBounds));
  }
  else {
    let parent = figure._owner;
    if( parent && parent.isGroup ) {
      if( parent.isGrouped ) {
        void 0;
        // Parent is a grouped Group
        // ... do not touch globalPre metrics
      }
      else {
        // Parent is a top level Group
        cache.copy('matrix'      ,'globalPreMatrix');
        cache.copy('vectorBounds','globalPreVectorBounds');
        cache.copy('bounds'      ,'globalPreBounds');
      }
    }
    else { // Top level figure
      cache.set('globalPreMatrix', new Matrix());
      cache.copy('preVectorBounds','globalPreVectorBounds');
      cache.copy('preBounds'      ,'globalPreBounds'      );
    }
  }

  if( metrics.GlobalMatrix ) {
    cache.set('globalMatrix'      ,Matrix.fromCore(metrics.GlobalMatrix));
    cache.set('globalVectorBounds',Bounds.fromCore(metrics.GlobalVectorBounds));
    cache.set('globalBounds'      ,Bounds.fromCore(metrics.GlobalBounds));

    if( Config.materialBounds && ! metrics.GlobalMaterialBounds ) {
      console.log('GlobalMaterialBounds not present');
    }
    cache.set('globalMaterialBounds',Bounds.fromCore(metrics.GlobalMaterialBounds || metrics.GlobalBounds));
  }
  else {
    let parent = figure._owner;
    if( parent && parent.isGroup ) {
      void 0;
      // Parent is a group
      // ... do not touch global metrics
    }
    else { // Top level figure
      cache.copy('matrix'        ,'globalMatrix'        );
      cache.copy('vectorBounds'  ,'globalVectorBounds'  );
      cache.copy('bounds'        ,'globalBounds'        );
      cache.copy('materialBounds','globalMaterialBounds');
    }
  }

  // [TODO] Reference Point, this will be removed as soon as we can remove the bounds based
  // pin system. I am not doing this for this release to avoid disruption.
  const referencePoint = Point.fromCore(metrics.RefPoint);
  const preReferencePoint = referencePoint.transformed(cache.get('matrix').inverted());
  cache.set('preReferencePoint',preReferencePoint);

  // Used for concrete font size mechanics
  const metrics_PreScale = metrics.PreAccumulatedScale;
  const preScale = metrics_PreScale ? Point.fromCore(metrics_PreScale) : new Point(1,1);
  figure._computedPreScale = preScale;
};

function parentGlobalMatrix( figure ) {
  const model = {};
  if( ! Config.computeFigureGeometryLocally || ! figure.supportsLocalMetrics() ) {
    const parent = figure._owner;
    if( parent && parent.isGroup ) {
      model.ParentGlobalMatrix = parent.globalMatrix_().toCore();
      if( parent.isGrouped ) {
        model.ParentGlobalPreMatrix = parent.globalPreMatrix_().toCore();
      }
    }
  }
  return model;
}

let requestCoreToProcessFigure = function(figureModel) {
  return Cx.Core.Figure.process$({ Figures: [ figureModel ] }) .then( m => m.Figures[0] );
};

Figure.processUsingServer$ = function(figure,options) {
  // console.log(figure.toXmlString( Config.serverCache ));

  const processModel = assign({
     Cdl: figure.toXmlString( Config.serverCache ),
     GetOutput: options.getOutput,
     StoreInCache: options.storeInCache,
     GetMaterialBounds: Config.materialBounds
  }, parentGlobalMatrix( figure ) );

  return requestCoreToProcessFigure(processModel) .then( figureModel => {

  parseCoreMetrics( figure, figureModel.Metrics );

  const cachedCdl = figureModel.CachedCdl;
  figure.cacheId = cachedCdl ? cachedCdl.CacheId : null;

  const outputFigureXml = figureModel.OutputCdl ;
  let outputFigure = null;
  if( outputFigureXml ) {
    outputFigure = Figure.fromXmlString(outputFigureXml);
    figure.outputFigure = outputFigure;

    // Complete Raster Metadata avoiding possible processing later
    if( figure.isRaster ) {
      if( figure._width === null ) {
        const width = outputFigure._width;
        if( width !== 0 ) {
          figure._width = width;
        }
      }
      if( figure._height === null ) {
        const height = outputFigure._height;
        if( height !== null ) {
          figure._height = height;
        }
      }
    }
  }
  else if( figure.isProxyGroup ) {
    // We create an outline output figure so our bounds system will work correctly
    outputFigure = Figure.createProxyGroupOutput(figure,figure.preBounds_(),figure.prePin_());
  }
  figure.outputFigure = outputFigure;

  return figure;
  });
};

Figure.measureUsingCore$ = function( figure ) {
  // console.log('Update measurements in the server');

  const processModel = assign({
     CachedCdl: Figure.cacheModel(figure),
     GetOutput: false
  }, parentGlobalMatrix(figure) );

  return requestCoreToProcessFigure(processModel) .then( figureModel => {

  // Return a function so Grafika can decide when to apply the result
  // We want to ask the server for the processing right away, but the
  // outputFigure and metrics needs to be applied in the order the
  // commits were issued. This lets users generate independent actions
  // without risk of invalid states

  // console.log('Metrics arrived')
  parseCoreMetrics( figure, figureModel.Metrics );

  return figure;

  });
};

// We need to implement these functions using javascript and C++ modules

Figures.toCoreModel = function( figures ) {
  return map( figures, f => Figure.toCoreModel(f) );
};

Figures.fromCoreModel = function(model) {
  return map( model.Figures, f => Figure.fromXmlString(f.Cdl) );
};

// @action: Cx.E.ReshapeAction
Figures.reshape$ = function( figures, action) {
  return Cx.Core.Figure.reshape$({
    Figures: Figures.toCoreModel(figures), Action: action
  }).then(Figures.fromCoreModel);
};

function condensedReshape$(figures,action) {
  return Figures.reshape$(figures,action) .then( first );
}

Figures.combine$ = function( figures ) {
  return condensedReshape$(figures,'Combine');
};

Figures.weld$ = function( figures ) {
  return condensedReshape$(figures,'Weld');
};

Figures.frontMinusBack$ = function( figures ) {
  return condensedReshape$(figures,'FrontMinusBack');
};

Figures.backMinusFront$ = function( figures ) {
  return condensedReshape$(figures,'BackMinusFront');
};

Figures.condense$ = function( figures ) {
  return condensedReshape$(figures,'Condense');
};

Figures.cutOutline$ = function( figures,options) {
  figures = { Figures: Figures.toCoreModel(figures) };
  return Cx.Core.Figure.cutOutline$(
    options ? assign(figures,objectToCoreCase(options)) : figures
  ).then(Figures.fromCoreModel);
};

Figures.rasterize$ = function( figures,options) {
  figures = { Figures: Figures.toCoreModel(figures) };
  return Cx.Core.Figure.rasterize$(
    options ? assign(figures,objectToCoreCase(options)) : figures
  ).then(Figures.fromCoreModel);
};

Figures.vectorize$ = function( figures,options) {
  figures = { Figures: Figures.toCoreModel(figures) };
  return Cx.Core.Figure.vectorize$(
    options ? assign(figures,objectToCoreCase(options)) : figures
  ).then(Figures.fromCoreModel);
};

Figures.colorAt$ = function( figures,point ) {
  if( ! isArray(figures) ) {
    figures = [figures];
  }
  return Cx.Core.Figure.colorAt$({
    Figures: Figures.toCoreModel(figures),
    Point: point.toCore()
  }).then( colorModel => {
    if(!colorModel) {
      return null;
    }
    const argb = colorModel.ARGB;
    return Color({ alpha: parseInt(argb.substring(0, 2),16)/255, rgb: argb.substring(2, argb.length) });
  });
};



/** WEBPACK FOOTER **
 ** ./src/resources/core-util.js
 **/